import {
  useQuery,
  useMutation,
  UseQueryOptions,
  UseMutationOptions,
  UseMutateFunction,
} from '@tanstack/react-query';
import UGCDraftService from '../../services/ugc-draft';
import {
  UGCDraftResponse,
  UGCDraft,
  UGCDraftsListResponse,
} from '../../models/ugc-draft/types';

export const UGC_DRAFT_QUERY_KEY = ['ugcDraft'];
export const UGC_DRAFTS_LIST_QUERY_KEY = ['ugcDraftsList'];

type UgcDraftMutationReturnType = {
  isWorking: boolean;
  mutate: UseMutateFunction<UGCDraftResponse, Error, UGCDraft>;
};

export type QueryResponse<T> = {
  isLoading: boolean;
  errorMessage?: string;
  error?: Error | null;
  data?: T;
  isSuccess?: boolean;
  invalidateQuery?: () => void;
  isFetching?: boolean | undefined;
};

export const useUgcDraftsListQuery = (
  options?: Omit<
    UseQueryOptions<
      UGCDraftsListResponse,
      Error,
      UGCDraftsListResponse,
      string[]
    >,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<
    UGCDraftsListResponse,
    Error,
    UGCDraftsListResponse,
    string[]
  >({
    queryKey: UGC_DRAFTS_LIST_QUERY_KEY,
    queryFn: async () => {
      const response = await UGCDraftService.index();
      return response.data;
    },
    ...options,
  });
};

export const useUgcDraftQuery = (
  id: number | undefined,
  options?: Omit<
    UseQueryOptions<UGCDraftResponse, Error, UGCDraftResponse, string[]>,
    'queryKey' | 'queryFn'
  >
) => {
  return useQuery<UGCDraftResponse, Error, UGCDraftResponse, string[]>({
    queryKey: [...UGC_DRAFT_QUERY_KEY, String(id)],
    queryFn: async () => {
      if (!id) {
        throw new Error('An ID is required');
      }

      const service = new UGCDraftService(id);
      const response = await service.fetch();

      return response.data;
    },
    enabled: !!id,
    ...options,
  });
};

export const useCreateUgcDraftMutation = (
  options?: Omit<
    UseMutationOptions<UGCDraftResponse, Error, UGCDraft>,
    'mutationFn'
  >
): UgcDraftMutationReturnType => {
  const { isLoading, mutate } = useMutation<UGCDraftResponse, Error, UGCDraft>({
    mutationFn: async (data: UGCDraft) => {
      const service = new UGCDraftService(undefined);
      const response = await service.create(data);
      return response.data;
    },
    ...options,
  });

  return { isWorking: isLoading, mutate };
};

export const useUpdateUgcDraftMutation = (
  id?: number,
  options?: Omit<
    UseMutationOptions<UGCDraftResponse, Error, UGCDraft>,
    'mutationFn'
  >
): UgcDraftMutationReturnType => {
  const { isLoading, mutate } = useMutation<UGCDraftResponse, Error, UGCDraft>({
    mutationFn: async (data: UGCDraft) => {
      if (!id) throw new Error('An ID is required');

      const service = new UGCDraftService(id);
      const response = await service.update(data);
      return response.data;
    },
    ...options,
  });
  return { isWorking: isLoading, mutate };
};

export const useUpdateUgcDraftStatusMutation = (
  id?: number,
  options?: Omit<
    UseMutationOptions<UGCDraftResponse, Error, UGCDraft>,
    'mutationFn'
  >
): UgcDraftMutationReturnType => {
  const { isLoading, mutate } = useMutation<UGCDraftResponse, Error, UGCDraft>({
    mutationFn: async (data: UGCDraft) => {
      if (!id) throw new Error('An ID is required');

      const service = new UGCDraftService(id);
      const response = await service.updateStatus(data.status);
      return response.data;
    },
    ...options,
  });
  return { isWorking: isLoading, mutate };
};
